// src/utils/loopsHelpers.js
export function safeLoopKey(obj) {
  // flow_loops items look like: { "Loop 1": [ ... ] }
  // return the first key
  if (!obj || typeof obj !== "object") return null;
  const keys = Object.keys(obj);
  return keys.length ? keys[0] : null;
}

export function loopIndexFromKey(k) {
  // Handles: "Loop 1", "loop 1", "1", 1, etc.
  const s = String(k ?? "").trim();
  const m = s.match(/(\d+)/);
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

export function sortLoopsByIndex(loops = []) {
  return (loops || []).slice().sort((a, b) => {
    const ai = loopIndexFromKey(a?.loopName);
    const bi = loopIndexFromKey(b?.loopName);
    if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
    return String(a?.loopName || "").localeCompare(String(b?.loopName || ""));
  });
}

export function computeLoopPeaks(points) {
  // points: [{x: volume, y: flow}, ...]
  let fExPeak = -Infinity,
    vExPeak = null;
  let fInPeak = Infinity,
    vInPeak = null;

  for (const p of points || []) {
    if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) continue;

    if (p.y > 0 && p.y > fExPeak) {
      fExPeak = p.y;
      vExPeak = p.x;
    }
    if (p.y < 0 && p.y < fInPeak) {
      fInPeak = p.y;
      vInPeak = p.x;
    }
  }

  const hasEx = Number.isFinite(fExPeak) && Number.isFinite(vExPeak);
  const hasIn = Number.isFinite(fInPeak) && Number.isFinite(vInPeak);

  return {
    hasPeaks: hasEx && hasIn,
    vExPeak,
    fExPeak,
    vInPeak,
    fInPeak,
  };
}

// src/utils/loopsHelpers.js

export function buildFlowVolumeDatasets(recordJson) {
  // NEW MPM flow-loop result shape:
  // {
  //   flowLoop: {
  //     breaths: [
  //       {
  //         breathNumber,
  //         metrics: { isSpirometryManeuverBreath },
  //         plotData: { volumeTrace: [], flowTrace: [] }
  //       }
  //     ]
  //   }
  // }
  const breaths = Array.isArray(recordJson?.flowLoop?.breaths)
    ? recordJson.flowLoop.breaths
    : Array.isArray(recordJson?.breaths)
      ? recordJson.breaths
      : [];

  if (breaths.length) {
    return breaths
      .map((breath, index) => {
        const volume = Array.isArray(breath?.plotData?.volumeTrace)
          ? breath.plotData.volumeTrace
          : [];

        const flow = Array.isArray(breath?.plotData?.flowTrace)
          ? breath.plotData.flowTrace
          : [];

        const m = Math.min(volume.length, flow.length);
        const points = [];

        for (let j = 0; j < m; j += 1) {
          const x = Number(volume[j]);
          const y = Number(flow[j]);

          if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

          points.push({ x, y });
        }

        return {
          loopName: `Breath ${breath?.breathNumber ?? index + 1}`,
          breathNumber: breath?.breathNumber ?? index + 1,
          sourceBreath: breath,
          points,
        };
      })
      .filter((loop) => loop.points.length > 1);
  }

  // LEGACY shape:
  // {
  //   flow_loops: [{ "Loop 1": [...] }],
  //   volume_loops: [{ "Loop 1": [...] }]
  // }
  const flowLoops = Array.isArray(recordJson?.flow_loops)
    ? recordJson.flow_loops
    : [];

  const volLoops = Array.isArray(recordJson?.volume_loops)
    ? recordJson.volume_loops
    : [];

  const n = Math.min(flowLoops.length, volLoops.length);
  const loops = [];

  for (let i = 0; i < n; i += 1) {
    const flowObj = flowLoops[i];
    const volObj = volLoops[i];

    const flowKey = safeLoopKey(flowObj);
    const volKey = safeLoopKey(volObj);

    const loopName = flowKey || volKey || `Loop ${i + 1}`;
    const flow = Array.isArray(flowObj?.[flowKey]) ? flowObj[flowKey] : [];
    const vol = Array.isArray(volObj?.[volKey]) ? volObj[volKey] : [];

    const m = Math.min(flow.length, vol.length);
    const points = [];

    for (let j = 0; j < m; j += 1) {
      const x = Number(vol[j]);
      const y = Number(flow[j]);

      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

      points.push({ x, y });
    }

    loops.push({
      loopName,
      breathNumber: i + 1,
      sourceBreath: null,
      points,
    });
  }

  return loops.filter((loop) => loop.points.length > 1);
}

export function fmtNumber(v, dp = 3) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toFixed(dp);
}

export function computeGlobalAxisBounds(loops, padFrac = 0.05) {
  let xMin = Infinity,
    xMax = -Infinity,
    yMin = Infinity,
    yMax = -Infinity;

  for (const loop of loops || []) {
    for (const p of loop.points || []) {
      if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) continue;
      if (p.x < xMin) xMin = p.x;
      if (p.x > xMax) xMax = p.x;
      if (p.y < yMin) yMin = p.y;
      if (p.y > yMax) yMax = p.y;
    }
  }

  if (![xMin, xMax, yMin, yMax].every(Number.isFinite)) return null;

  // Optional padding so traces don't sit on the border
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;

  const xPad = xRange * padFrac;
  const yPad = yRange * padFrac;

  return {
    xMin: xMin - xPad,
    xMax: xMax + xPad,
    yMin: yMin - yPad,
    yMax: yMax + yPad,
  };
}

export function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

export function rotatePoint(p, angleRad) {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return { x: p.x * c - p.y * s, y: p.x * s + p.y * c };
}

export function buildFallbackLoopTimesSeconds(nLoops, durationSeconds) {
  if (!nLoops || nLoops <= 0) return [];
  const d = Number(durationSeconds);
  if (!Number.isFinite(d) || d <= 0) {
    // fallback: index-as-time (still stable)
    return Array.from({ length: nLoops }, (_, i) => i);
  }
  // evenly distribute loop “centers” across the recording
  const step = d / nLoops;
  return Array.from({ length: nLoops }, (_, i) => i * step);
}

export function findActiveLoopIndexBySecond(loopTimesS, tSec) {
  if (!loopTimesS?.length) return 0;
  // find last loop with time <= tSec
  let lo = 0,
    hi = loopTimesS.length - 1,
    ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (loopTimesS[mid] <= tSec) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

export function normalizeTimesToSeconds(times, durationSeconds) {
  const arr = Array.isArray(times) ? times.map((v) => Number(v)) : [];
  const clean = arr.filter((v) => Number.isFinite(v));
  if (!clean.length) return [];

  // Heuristic: if values look like ms (e.g., max >> duration), convert to seconds.
  const maxT = Math.max(...clean);
  const dur = Number(durationSeconds);

  const looksLikeMs =
    Number.isFinite(dur) && dur > 0 ? maxT > dur * 5 : maxT > 500; // fallback

  return looksLikeMs ? clean.map((v) => v / 1000) : clean;
}

/**
 * Reveal points progressively by time:
 * - `times` is the TOP-LEVEL per-sample time array (same index as points inside each loop).
 * - For any loop, point index j maps to times[j].
 * - At playback time tSec, we show points [0..visibleIdx] for the active loop and trail loops.
 */

export function lastIndexAtOrBefore(timesSortedAsc, tSec) {
  let lo = 0;
  let hi = timesSortedAsc.length - 1;
  let ans = -1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (timesSortedAsc[mid] <= tSec) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}
